// ─────────────────────────────────────────────
// Oike — Finanzas › Resultados, entrega B1 (pure; tested by scripts/test-business-results.mjs). Agreed with Ale on
// 04-11-2026. Only the owner. Amounts as REGISTERED; the tax treatment is not defined yet, so nothing here is «net of
// taxes». Recognized income, EBITDA and margin are NOT available in B1: the screen says what is missing.
// - Sales: one per contract (a chain of quote versions, its latest version ever accepted), in the month of its
//   acceptance. One-off amounts apart from monthly commitments. Gross sales apart from cancellations (in the month
//   of the cancellation); a later cancellation or correction never silently changes an earlier month: it is shown
//   there as a trace. An acceptance corrected as registered by error does not count, and says so.
// - Ticket: one-off sales over contracts WITH a one-off amount (a monthly-only contract never lowers it with a
//   zero); monthly commitments averaged apart. Each with its base (amount and number of contracts). By service only
//   with classifications Ale confirmed; the rest is «Sin clasificar» with a proposal (serviceKeyOf), never attributed.
// - Expenses: business only. «Gastos registrados por fecha» (the expense's date), by class: the class comes from
//   the category Ale confirmed, or from an exception on that expense. Indirect taxes are their own class with a
//   pending treatment (never excluded or included by default). Annual subscriptions and assets are flagged: their
//   time allocation is not decided.
// ─────────────────────────────────────────────
import { chainsOf } from './quoteChains.js';
import { acceptanceMarksOf, FACT_FIELDS } from './pipelineFacts.js';
import { quoteLinesOf, serviceKeyOf } from './serviceStart.js';
import { isTestName } from './testRecords.js';

const cents = (n) => Math.round(Number(n || 0) * 100);
const money = (c) => Math.round(c) / 100;
const DAY_RE = /\d{4}-\d{2}-\d{2}/;

export const SERVICE_KEYS = [
  ['diagnostic', 'Oike Diagnostic'], ['systemDesign', 'Oike System Design'], ['enablement', 'Oike Enablement'],
  ['advisory', 'Oike Advisory'], ['coaching', 'Acompañamiento'], ['other', 'Otro servicio'],
].map(([key, label]) => ({ key, label }));
export const serviceLabel = (k) => SERVICE_KEYS.find(s => s.key === k)?.label || k;

// ── Sales ──
/** The day of a cancellation (the day Ale gave, or the day she recorded it) and of an acceptance corrected as error. */
export function correctionsOf(note) {
  const t = String(note || '');
  const lines = t.split('\n');
  const cancelLine = lines.find(l => l.includes('[aceptación cancelada por el cliente]'));
  const errorLine = lines.find(l => l.includes('[aceptación corregida: registrada por error]'));
  const recorded = (l) => (l?.match(/\((\d{4}-\d{2}-\d{2}) \d{2}:\d{2} UTC/) || [])[1] || '';
  return {
    cancelledOn: cancelLine ? ((cancelLine.match(/canceló el (\d{4}-\d{2}-\d{2})/) || [])[1] || recorded(cancelLine)) : '',
    cancelRecordedOn: recorded(cancelLine),
    correctedOn: recorded(errorLine),
    acceptedOnBeforeError: errorLine ? ((errorLine.match(/aceptada (\d{4}-\d{2}-\d{2})/) || [])[1] || '') : '',
  };
}

/**
 * One sale per contract: { chainId, versionId, name, state: accepted|cancelled|corrected, acceptedOn, cancelledOn,
 * correctedOn, currency, currencyMissing, once, monthly, lines }. Test records are left out.
 */
export function salesOf(intakes = []) {
  const out = [];
  for (const chain of chainsOf(intakes)) {
    const ever = chain.versions.filter(v => {
      const f = v.record.fields || {};
      const m = acceptanceMarksOf(f[FACT_FIELDS.factsNote]);
      return v.state === 'accepted' || f[FACT_FIELDS.wonAt] || m.cancelled || m.error;
    });
    if (!ever.length) continue;
    const v = ever[ever.length - 1];
    const f = v.record.fields || {};
    if (isTestName(String(f['Name'] || ''))) continue;
    const m = acceptanceMarksOf(f[FACT_FIELDS.factsNote]);
    const c = correctionsOf(f[FACT_FIELDS.factsNote]);
    const state = v.state === 'accepted' ? 'accepted' : m.cancelled ? 'cancelled' : m.error ? 'corrected' : 'accepted';
    const acceptedOn = String(f[FACT_FIELDS.wonAt] || '').slice(0, 10) || c.acceptedOnBeforeError;
    const lines = quoteLinesOf(f).map(l => ({ key: `${v.id}:${l.lineKey}`, lineKey: l.lineKey, service: l.service, once: money(cents(l.onceFee)), monthly: money(cents(l.monthlyFee)), suggested: l.serviceKey }));
    out.push({
      chainId: chain.id, versionId: v.id, name: String(f['Name'] || ''), state, acceptedOn: DAY_RE.test(acceptedOn) ? acceptedOn : '',
      cancelledOn: state === 'cancelled' ? c.cancelledOn : '', correctedOn: state === 'corrected' ? c.correctedOn : '',
      currency: f['Quote Currency'] || 'USD', currencyMissing: !f['Quote Currency'],
      once: money(lines.reduce((s, l) => s + cents(l.once), 0)), monthly: money(lines.reduce((s, l) => s + cents(l.monthly), 0)), lines,
    });
  }
  return out;
}

const inMonth = (day, month) => !!day && day.slice(0, 7) === month;
const item = (s) => ({ id: s.versionId, chainId: s.chainId, name: s.name, day: s.acceptedOn, once: s.once, monthly: s.monthly, state: s.state, cancelledOn: s.cancelledOn, correctedOn: s.correctedOn });
/**
 * A month, per currency: gross sales (accepted in the month, also the ones cancelled later, marked), cancellations
 * registered in the month, and acceptances of the month corrected later as error (a trace; they do not count).
 */
export function salesForMonth(sales = [], month) {
  const by = {};
  const slot = (c) => (by[c] = by[c] || { gross: { once: 0, monthly: 0, count: 0, items: [] }, cancellations: { once: 0, monthly: 0, count: 0, items: [] }, corrected: [] });
  for (const s of sales) {
    if (s.state === 'corrected') { if (inMonth(s.acceptedOn, month)) slot(s.currency).corrected.push(item(s)); continue; }
    if (inMonth(s.acceptedOn, month)) { const g = slot(s.currency).gross; g.once += cents(s.once); g.monthly += cents(s.monthly); g.count++; g.items.push(item(s)); }
    if (s.state === 'cancelled' && inMonth(s.cancelledOn, month)) { const x = slot(s.currency).cancellations; x.once += cents(s.once); x.monthly += cents(s.monthly); x.count++; x.items.push(item(s)); }
  }
  for (const b of Object.values(by)) for (const k of ['gross', 'cancellations']) { b[k].once = money(b[k].once); b[k].monthly = money(b[k].monthly); }
  const undated = sales.filter(s => s.state !== 'corrected' && !s.acceptedOn).map(item);
  return { month, byCurrency: by, undated };
}

/** The confirmed service of a line (Ale's classification), or null. */
const confirmedOf = (classes, key) => (classes?.[key]?.serviceKey && SERVICE_KEYS.some(s => s.key === classes[key].serviceKey) ? classes[key].serviceKey : null);

/**
 * Ticket over the contracts accepted in [from, to], per currency. One-off: contracts with a one-off amount;
 * monthly: contracts with a monthly commitment. Cancelled ones are left out of the ticket and shown apart (with the
 * figure including them); corrected ones never count. By service: only confirmed classifications.
 */
export function ticketOf(sales = [], { from, to, classes = {} }) {
  const by = {};
  const slot = (c) => (by[c] = by[c] || { once: { sumC: 0, count: 0, items: [] }, monthly: { sumC: 0, count: 0, items: [] }, monthlyOnly: 0, cancelled: { count: 0, onceC: 0, items: [] }, services: {}, unclassified: [] });
  for (const s of sales) {
    if (s.state === 'corrected' || !s.acceptedOn || s.acceptedOn < from || s.acceptedOn > to) continue;
    const b = slot(s.currency);
    if (s.state === 'cancelled') { b.cancelled.count++; b.cancelled.onceC += cents(s.once); b.cancelled.items.push(item(s)); continue; }
    if (s.once > 0) { b.once.sumC += cents(s.once); b.once.count++; b.once.items.push(item(s)); }
    if (s.monthly > 0) { b.monthly.sumC += cents(s.monthly); b.monthly.count++; b.monthly.items.push(item(s)); if (!(s.once > 0)) b.monthlyOnly++; }
    for (const l of s.lines) {
      const k = confirmedOf(classes, l.key);
      if (!k) { b.unclassified.push({ ...l, sale: s.name, versionId: s.versionId }); continue; }
      const sv = (b.services[k] = b.services[k] || { onceC: 0, onceCount: 0, monthlyC: 0, monthlyCount: 0, contracts: new Set() });
      sv.contracts.add(s.chainId);
      if (l.once > 0) { sv.onceC += cents(l.once); sv.onceCount++; }
      if (l.monthly > 0) { sv.monthlyC += cents(l.monthly); sv.monthlyCount++; }
    }
  }
  const out = {};
  for (const [c, b] of Object.entries(by)) {
    const avg = (sumC, n) => (n ? money(Math.round(sumC / n)) : null);
    out[c] = {
      once: { amount: money(b.once.sumC), count: b.once.count, average: avg(b.once.sumC, b.once.count), items: b.once.items },
      monthly: { amount: money(b.monthly.sumC), count: b.monthly.count, average: avg(b.monthly.sumC, b.monthly.count), items: b.monthly.items, monthlyOnly: b.monthlyOnly },
      cancelled: { count: b.cancelled.count, once: money(b.cancelled.onceC), items: b.cancelled.items,
        averageIncluding: avg(b.once.sumC + b.cancelled.onceC, b.once.count + b.cancelled.items.filter(i => i.once > 0).length) },
      services: Object.fromEntries(Object.entries(b.services).map(([k, v]) => [k, { label: serviceLabel(k), contracts: v.contracts.size, once: { amount: money(v.onceC), count: v.onceCount, average: avg(v.onceC, v.onceCount) }, monthly: { amount: money(v.monthlyC), count: v.monthlyCount, average: avg(v.monthlyC, v.monthlyCount) } }])),
      unclassified: b.unclassified,
    };
  }
  return out;
}

/** Lines of accepted (or cancelled) contracts without a confirmed service, with the proposal to review. */
export function serviceProposals(sales = [], classes = {}) {
  return sales.filter(s => s.state !== 'corrected').flatMap(s => s.lines.filter(l => !confirmedOf(classes, l.key)).map(l => ({
    key: l.key, service: l.service, sale: s.name, versionId: s.versionId, currency: s.currency, once: l.once, monthly: l.monthly,
    proposal: l.suggested && l.suggested !== 'other' ? l.suggested : null,
  })));
}

// ── Expenses ──
export const EXPENSE_CLASSES = [
  { key: 'operating', label: 'Gasto operativo' },
  { key: 'interest', label: 'Intereses de financiación' },
  { key: 'income_tax', label: 'Impuesto sobre beneficios' },
  { key: 'indirect_tax', label: 'Impuestos indirectos (tratamiento pendiente)' },
  { key: 'asset', label: 'Compra de activo' },
  { key: 'outside', label: 'Fuera del resultado' },
];
export const classLabel = (k) => EXPENSE_CLASSES.find(c => c.key === k)?.label || k;
/**
 * A proposal per category of the default list (never applied by itself). Mixed categories («Equipamiento», «Tasas e
 * impuestos», «Otros») have no proposal: they need Ale's decision or an exception per expense.
 */
const PROPOSALS = { software: 'operating', learning: 'operating', professional: 'operating', marketing: 'operating', travel: 'operating', office: 'operating', banking: 'operating' };
const PROPOSAL_NOTES = {
  banking: 'Comisiones de bancos y de cobro son gasto operativo; si hubiera intereses de un préstamo, van en su propia categoría o como excepción.',
  equipment: 'Puede ser una compra de activo o un gasto operativo menor: decidilo por categoría o por gasto.',
  taxes: 'Mezcla impuestos distintos (indirectos, sobre beneficios, tasas): conviene separarla en categorías o decidir por gasto.',
  other: 'Sin propuesta: revisalo por gasto.',
};
export const classProposal = (categoryKey) => ({ class: PROPOSALS[categoryKey] || null, note: PROPOSAL_NOTES[categoryKey] || '' });
/** Categories that could be added (optional, never renaming existing ones). */
export const PROPOSED_NEW_CATEGORIES = [
  { label: 'Intereses de financiación', class: 'interest' },
  { label: 'Impuesto sobre beneficios', class: 'income_tax' },
  { label: 'Impuestos indirectos (IVA, IT…)', class: 'indirect_tax' },
  { label: 'Compra de activos', class: 'asset' },
];

/** The class of one business expense: its own exception first, then its category's confirmed class. */
export function classOfExpense(x, categoryClasses = {}) {
  const ex = x.details?.resultClass;
  if (ex?.class && EXPENSE_CLASSES.some(c => c.key === ex.class)) return { class: ex.class, source: 'exception' };
  const cc = categoryClasses?.[x.category]?.class;
  if (cc && EXPENSE_CLASSES.some(c => c.key === cc)) return { class: cc, source: 'category' };
  return { class: null, source: null };
}

const monthsIn = (from, to) => {
  const out = []; let [y, m] = from.slice(0, 7).split('-').map(Number); const [ty, tm] = to.slice(0, 7).split('-').map(Number);
  while (y < ty || (y === ty && m <= tm)) { out.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } }
  return out;
};
const coversSeveralMonths = (x) => x.recurrence === 'yearly' || /^\d{4}$/.test(String(x.period || ''));

/**
 * «Gastos registrados por fecha» (business): per month (expense date) and currency, by class, with the records.
 * Cancelled expenses are left out; expenses «requiere revisión» stay in with a flag. Coverage: how much is
 * classified, the exceptions, months without registered expenses, multi-month expenses (allocation pending), tax
 * lines inside expenses (treatment pending) and expenses paid with personal money (counted once).
 */
export function expensesByDate(list = [], { from, to, categoryClasses = {}, categories = [] }) {
  const months = monthsIn(from, to);
  const by = {};
  const slot = (cur) => (by[cur] = by[cur] || { total: 0, classes: {}, unclassified: { amount: 0, items: [] }, months: Object.fromEntries(months.map(m => [m, 0])) });
  const flags = { exceptions: [], multiMonth: [], review: [], taxLines: {}, personalMoney: {} };
  const withExpenses = new Set();
  let count = 0, classified = 0;
  for (const x of list) {
    if (x.status === 'cancelled' || x.state?.state === 'cancelled') continue;
    if (!x.incurredOn || x.incurredOn < from || x.incurredOn > to) continue;
    count++; withExpenses.add(x.incurredOn.slice(0, 7));
    const b = slot(x.currency);
    const c = classOfExpense(x, categoryClasses);
    const it = { id: x.id, concept: x.concept, supplier: x.supplier, day: x.incurredOn, amount: x.amount, category: categories.find(k => k.key === x.category)?.label || x.category, source: c.source };
    b.total += cents(x.amount); b.months[x.incurredOn.slice(0, 7)] += cents(x.amount);
    if (c.class) {
      classified++;
      const k = (b.classes[c.class] = b.classes[c.class] || { amount: 0, items: [] });
      k.amount += cents(x.amount); k.items.push(it);
    } else { b.unclassified.amount += cents(x.amount); b.unclassified.items.push(it); }
    if (c.source === 'exception') flags.exceptions.push({ ...it, class: c.class, reason: x.details?.resultClass?.reason || '' });
    if (coversSeveralMonths(x)) flags.multiMonth.push({ ...it, period: x.period || '', recurrence: x.recurrence });
    if (x.state?.review?.length) flags.review.push({ ...it, reasons: x.state.review });
    const tl = (x.details?.taxes?.lines || []).filter(l => l.amount !== null && l.amount !== undefined);
    if (tl.length) { const t = (flags.taxLines[x.currency] = flags.taxLines[x.currency] || { amount: 0, items: [] }); t.amount += tl.reduce((s, l) => s + cents(l.amount), 0); t.items.push({ ...it, status: x.details.taxes.status }); }
    const personal = (x.state?.payments || []).filter(p => p.status === 'confirmed' && p.paidFrom === 'personal');
    if (personal.length) { const pm = (flags.personalMoney[x.currency] = flags.personalMoney[x.currency] || { amount: 0, items: [] }); const a = personal.reduce((s, p) => s + cents(p.amount), 0); pm.amount += a; pm.items.push({ ...it, amount: money(a) }); }
  }
  for (const b of Object.values(by)) {
    b.total = money(b.total); b.unclassified.amount = money(b.unclassified.amount);
    for (const k of Object.values(b.classes)) k.amount = money(k.amount);
    for (const m of Object.keys(b.months)) b.months[m] = money(b.months[m]);
  }
  for (const t of Object.values(flags.taxLines)) t.amount = money(t.amount);
  for (const t of Object.values(flags.personalMoney)) t.amount = money(t.amount);
  return { from, to, byCurrency: by, count, classified, monthsWithout: months.filter(m => !withExpenses.has(m)), ...flags };
}

/** The categories (business) with their confirmed class or a proposal to review. */
export function categoryClassRows(categories = [], categoryClasses = {}) {
  return categories.filter(c => !c.archived).map(c => ({ key: c.key, label: c.label, confirmed: categoryClasses[c.key]?.class || null, ...classProposal(c.key) }));
}

// ── What is missing for recognized income and EBITDA (B2/B3) ──
export function pendingForResults({ proposals = [], categoryRows = [], expenses = null } = {}) {
  const unclassifiedCats = categoryRows.filter(r => !r.confirmed);
  return [
    { key: 'recognition', title: 'Criterio de reconocimiento de ingresos', status: 'postponed', text: 'Pospuesto: la contabilidad por reconocimiento no forma parte de Finanzas por ahora.' },
    { key: 'delivery', title: 'Registro de la prestación', status: 'postponed', text: 'Pospuesto: no registrás prestaciones aparte del pago y del trabajo en los procesos.' },
    { key: 'reconciliation', title: 'Conciliación de caja e ingresos', status: 'postponed', text: 'Pospuesto junto con el ingreso reconocido.' },
    { key: 'services', title: 'Servicio de cada línea vendida', status: proposals.length ? 'partial' : 'done', text: proposals.length ? `${proposals.length} línea(s) sin clasificación confirmada: no entran en cifras por servicio.` : 'Todas las líneas tienen servicio confirmado.' },
    { key: 'classes', title: 'Clase de cada gasto', status: unclassifiedCats.length || (expenses && expenses.count > expenses.classified) ? 'partial' : 'done', text: `${unclassifiedCats.length ? `${unclassifiedCats.length} categoría(s) sin clase confirmada. ` : 'Todas las categorías activas tienen clase. '}${expenses ? `${expenses.classified} de ${expenses.count} gasto(s) del período con clase.` : ''}` },
    { key: 'allocation', title: 'Imputación temporal de gastos', status: 'postponed', text: `Suscripciones anuales o gastos que cubren varios meses${expenses ? ` (${expenses.multiMonth.length} en el período)` : ''}: todavía sin criterio (entero o repartido).` },
    { key: 'assets', title: 'Activos y amortización', status: 'postponed', text: 'Vida útil y método de amortización sin definir: las compras de activo se listan, no se amortizan.' },
    { key: 'indirect', title: 'Impuestos indirectos', status: 'postponed', text: 'Tratamiento pendiente (recuperable o no recuperable) hasta definir desde dónde facturás.' },
    { key: 'tax_regime', title: 'País de facturación y criterio contable', status: 'postponed', text: 'Necesario para el impuesto sobre beneficios, los impuestos indirectos y la amortización fiscal.' },
    { key: 'personal_money', title: 'Gastos del negocio pagados con tu dinero', status: 'postponed', text: 'Se cuentan una vez como gastos del negocio. Si además se registra lo que el negocio te debe, está sin decidir.' },
  ];
}
